import { useCompany } from '@/contexts/useCompany';
import type { MemberRole } from '@/types/database';

/**
 * Role-based permissions for the active company (team accounts).
 *
 * Kept separate from `useFeature` on purpose: `CompanyProvider` itself calls
 * `useFeature('multiple_companies')`, so `useFeature` must NOT depend on
 * `useCompany` (that would create a hook cycle). Feature (plan) gating and
 * role gating are therefore composed at call sites, e.g.:
 *
 *   const { canSeeFinance } = useRole();
 *   const showFinance = canSeeFinance;
 *
 * The server (RLS) is always the final authority; these booleans only shape the
 * UI so users are not shown actions they cannot perform.
 */

const RANK: Record<MemberRole, number> = {
  viewer: 0,
  member: 1,
  admin: 2,
  owner: 3,
};

export interface UseRoleResult {
  role: MemberRole | null;
  isOwner: boolean;
  isAdmin: boolean;   // owner or admin
  isMember: boolean;  // member (can write, no finance/team)
  isViewer: boolean;  // read-only
  /** Create/edit quotes, clients and catalog. */
  canWrite: boolean;
  /** Delete quotes/clients/products. */
  canDelete: boolean;
  /** See and manage the finance and fiscal (NFS-e) modules. */
  canSeeFinance: boolean;
  /** Invite/remove members and manage roles. */
  canManageTeam: boolean;
  /** Edit or delete the company itself, and manage billing. */
  canManageCompany: boolean;
  /** Rank comparison against a minimum role. */
  hasAtLeast: (min: MemberRole) => boolean;
}

export function useRole(): UseRoleResult {
  const { currentRole } = useCompany();
  const role = currentRole;

  // Retrocompatibility: a null role means "not yet known" (initial load, or a
  // pre-team offline cache). Legacy single-user accounts are always owners, so
  // default permissions to owner rather than locking the UI. This can never
  // grant real access — RLS rejects any operation the membership doesn't allow.
  const effectiveRole: MemberRole = role ?? 'owner';

  const hasAtLeast = (min: MemberRole): boolean =>
    RANK[effectiveRole] >= RANK[min];

  const isAdmin = hasAtLeast('admin');   // owner or admin
  const canWrite = hasAtLeast('member'); // owner, admin or member

  return {
    role,
    isOwner: effectiveRole === 'owner',
    isAdmin,
    isMember: role === 'member',
    isViewer: role === 'viewer',
    canWrite,
    canDelete: isAdmin,
    canSeeFinance: isAdmin,
    canManageTeam: isAdmin,
    canManageCompany: effectiveRole === 'owner',
    hasAtLeast,
  };
}
